import {
  KullaniciDto,
  KullaniciFilterDto,
  KullaniciListItemDto,
  KullaniciFormDto,
  KullaniciTableSettingsDto,
  SirketSelectListItemDto,
  UserDto,
} from "@emutabakat-types/anaveri";
import {
  saveState,
  getPersistedState,
  LocalStorageStates,
} from "@/helpers/localStorageHelper";
import { model } from "@arkas/core";
import store from "@/store";

// to persist the app state in the local storage.
// when the redux state changes, local storage saves it too.
store.subscribe(() => {
  saveState("kullanici.state", {
    kullaniciModel: store.getState().kullaniciModel,
  });
});

interface KullaniciStateModelDto {
  model: KullaniciDto | undefined;
  list: KullaniciListItemDto[];
  sirketList: SirketSelectListItemDto[];
  seciliSirket: SirketSelectListItemDto | undefined;
  tableSettings: KullaniciTableSettingsDto;
  currentFilter: KullaniciFilterDto;
  currentUser: UserDto;
  success: boolean;
}

// Local Storage'dan initial state alınıyor.
const persistedState: string | null = getPersistedState(
  LocalStorageStates.kullaniciState
);
const kullaniciState = persistedState
  ? JSON.parse(persistedState).kullaniciModel
  : undefined;

const initialState: KullaniciStateModelDto = {
  model: undefined,
  list: [],
  sirketList: kullaniciState ? kullaniciState.sirketList : [],
  seciliSirket: kullaniciState ? kullaniciState.seciliSirket : undefined,
  tableSettings: {
    pageSize: 10,
    currentPage: 1,
  },
  success: false,
  currentFilter: { isActive: true, arkasKullanicisiMi: true },
  currentUser: kullaniciState
    ? (kullaniciState.currentUser as UserDto)
    : ({
        id: 0,
        isArkasUser: false,
        musteriId: 0,
        musteriUnvani: "",
        tercihEdilenDil: "tr",
      } as UserDto),
};

export default model({
  config: {
    apiName: "kullanici",
    name: "kullaniciModel",
    initialState,
  },
  reducers: {
    set(state, model: KullaniciDto | undefined) {
      return { ...state, model };
    },
    setCurrentFilter(state, currentFilter: KullaniciFilterDto) {
      return { ...state, currentFilter };
    },
    resetCurrentFilter(state) {
      const currentFilter: KullaniciFilterDto = initialState.currentFilter;
      return { ...state, currentFilter };
    },
    setTableSettings(state, tableSettings) {
      return { ...state, tableSettings };
    },
    resetTableSettings(state) {
      return {
        ...state,
        tableSettings: initialState.tableSettings,
      };
    },
    setSeciliSirket(state, seciliSirket) {
      return { ...state, seciliSirket };
    },
    setSirketList(state, sirketList) {
      return { ...state, sirketList };
    },
    setCurrentUser(state, currentUser) {
      return { ...state, currentUser };
    },
  },
  effects: {
    async getAllData(): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.getAsync<KullaniciDto[]>("GetAllDataAsync");
      return { list: response };
    },
    async getAllDataByFilter(
      model: KullaniciFilterDto
    ): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.postAsync<KullaniciListItemDto[]>(
        "GetAllDataByFilterAsync",
        model
      );
      return { list: response };
    },
    async getById(id: string): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.getAsync<KullaniciDto>(
        `GetByIdAsync/${id}`,
        undefined,
        { timeout: Number(process.env.ARKAS_APP_REQUEST_TIMEOUT) }
      );
      return { model: response };
    },
  },
  methods: {
    async getUserCompanies(): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.getAsync<SirketSelectListItemDto[]>(
        `GetUserCompaniesAsync`,
        undefined,
        { timeout: Number(process.env.ARKAS_APP_REQUEST_TIMEOUT) }
      );
      return { sirketList: response };
    },
    async updateActiveCompany(
      inputModel: SirketSelectListItemDto
    ): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.postAsync<SirketSelectListItemDto>(
        "UpdateActiveCompanyAsync",
        inputModel,
        {
          timeout: Number(process.env.ARKAS_APP_REQUEST_TIMEOUT),
        }
      );
      return { seciliSirket: response };
    },
    async getCurrentUser(): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.getAsync<UserDto>(
        `GetCurrentUserAsync`,
        undefined,
        { timeout: Number(process.env.ARKAS_APP_REQUEST_TIMEOUT) }
      );
      return { currentUser: response };
    },
    async getDosya(): Promise<{ success: boolean }> {
      const response = await this.getAsync<boolean>(`Download`, undefined, {
        timeout: Number(process.env.ARKAS_APP_REQUEST_TIMEOUT),
      });
      return { success: response };
    },

    async create(
      model: KullaniciFormDto
    ): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.postAsync<KullaniciDto>(
        "CreateAsync",
        model,
        {
          timeout: Number(process.env.ARKAS_APP_REQUEST_TIMEOUT),
        }
      );
      return { model: response };
    },
    async update(
      model: KullaniciFormDto
    ): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.postAsync<KullaniciDto>(
        "UpdateAsync",
        model,
        {
          timeout: Number(process.env.ARKAS_APP_REQUEST_TIMEOUT),
        }
      );
      return { model: response };
    },
    async delete(id: string): Promise<void> {
      await this.postAsync<KullaniciDto>(`DeleteAsync/${id}`, {});
    },
    async switchPreferredLanguage(
      id: string
    ): Promise<Partial<KullaniciStateModelDto>> {
      const response = await this.getAsync<UserDto>(
        `SwitchPreferredLanguageAsync/${id}`,
        { timeout: Number(process.env.ARKAS_APP_REQUEST_TIMEOUT) }
      );
      return { currentUser: response };
    },
  },
});
